import CategoryHeader from "./CategoryHeader";
import PresentationsList from "./PresentationsList";
import React, {
  memo,
  useEffect,
  useMemo,
  useState
  } from "react";
import { ApplicationStatus } from "../../../interfaces/enums";
import { ApplicationWithId } from "../../../interfaces/IApplications";
import { Box, Collapse } from "@mui/material";
import { CategoryWithId } from "../../../interfaces/ICategories";
import { isRegistrationOpen } from "../../../utils/utils";
import { sortPresentations } from "./presentations/presentationDisplayUtils";
import { useContestContext } from "../../../contexts/ContestContext";

interface ExpandableCategoryItemProps {
  category: CategoryWithId;
  application: ApplicationWithId;
  presentations: any[];
  onAddPresentation: (categoryId: string) => void;
  onEditPresentation: (presentation: any) => void;
  onDeletePresentation: (presentation: any) => void;
  forceCollapsed?: boolean;
}

function ExpandableCategoryItem({
  category,
  application,
  presentations,
  onAddPresentation,
  onEditPresentation,
  onDeletePresentation,
  forceCollapsed = false,
}: ExpandableCategoryItemProps) {
  const { contest } = useContestContext();

  // Filtruj i sortuj prezentacje dla tej kategorii - memoized
  const categoryPresentations = useMemo(() => {
    const filtered = presentations.filter((p) => p.categoryId === category.id);
    return sortPresentations(filtered, category.type);
  }, [presentations, category.id, category.type]);

  // Domyślnie rozwinięte jeśli są prezentacje, ale można wymusić zwinięcie
  const [expanded, setExpanded] = useState(categoryPresentations.length > 0 && !forceCollapsed);
  const canAddPresentation =
    application.status === ApplicationStatus.DRAFT && isRegistrationOpen(contest || undefined);
  // Aktualizuj stan gdy zmienia się forceCollapsed
  useEffect(() => {
    if (forceCollapsed) {
      setExpanded(false);
    } else if (categoryPresentations.length > 0) {
      setExpanded(true);
    }
  }, [forceCollapsed, categoryPresentations.length]);

  // Oblicz ilość zawodników w tej kategorii - memoized
  const participantsInCategory = useMemo(
    () =>
      categoryPresentations.reduce((total, presentation) => {
        return total + (presentation.participants?.length || 0);
      }, 0),
    [categoryPresentations],
  );

  const handleToggleExpand = () => setExpanded(!expanded);

  return (
    <Box
      id={`category-${category.id}`}
      sx={{ border: "1px solid #e0e0e0", borderRadius: 1, mb: 1 }}
    >
      {/* Główny wiersz kategorii */}
      <CategoryHeader
        category={category}
        application={application}
        participantsInCategory={participantsInCategory}
        categoryPresentationsCount={categoryPresentations.length}
        expanded={expanded}
        canAddPresentation={canAddPresentation}
        onAddPresentation={onAddPresentation}
        onToggleExpand={handleToggleExpand}
      />

      {/* Rozwijana sekcja z prezentacjami */}
      <Collapse in={expanded}>
        <Box sx={{ borderTop: "1px solid #e0e0e0", backgroundColor: "#fcfaf7" }}>
          <PresentationsList
            presentations={categoryPresentations}
            application={application}
            category={category}
            onEditPresentation={onEditPresentation}
            onDeletePresentation={onDeletePresentation}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

// Memoized component to prevent unnecessary re-renders
export default memo(ExpandableCategoryItem, (prevProps, nextProps) => {
  return (
    prevProps.category.id === nextProps.category.id &&
    prevProps.category.name === nextProps.category.name &&
    prevProps.category.day === nextProps.category.day &&
    prevProps.application.id === nextProps.application.id &&
    prevProps.application.status === nextProps.application.status &&
    prevProps.presentations.length === nextProps.presentations.length &&
    prevProps.forceCollapsed === nextProps.forceCollapsed
  );
});
